/**
 * Container Renderer - CSS Grid (Completely Simplified)
 * Native CSS Grid ohne Berechnungen
 */

import { Trash2, Plus } from 'lucide-react';
import type { ContainerElement } from '../../types/template.types';
import { ElementRenderer } from './element-renderer';

export interface ContainerRendererProps {
  container: ContainerElement;
  containerPath: number[];
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
  onInsertClick: (containerPath: number[], rowIndex: number, position: 'above' | 'below', cellRow?: number, cellColumn?: number) => void;
  onDelete: (elementId: string) => void;
  fontFamily: string;
  fontSize: number;
  fontColor: string;
  isDeletable?: boolean;
  topMargin?: number;
  bottomMargin?: number;
  rootContainerIds?: string[]; // IDs of root containers
}

export function ContainerRenderer({
  container,
  containerPath,
  selectedElementId,
  onSelectElement,
  onInsertClick,
  onDelete,
  fontFamily,
  fontSize,
  fontColor,
  isDeletable = true,
  rootContainerIds = [],
}: ContainerRendererProps) {
  const isSelected = container.id === selectedElementId;
  
  // Check if this is a header or footer container
  const isHeaderOrFooter = rootContainerIds.includes(container.id);

  const { rows, columns } = container;
  const children = Array.isArray(container.children) ? container.children : [];
  const gap = container.gap ?? 1;

  const resolveBorderValue = (primary: number | undefined, legacyKey: 'border-left' | 'border-top' | 'border-right' | 'border-bottom') => {
    if (typeof primary === 'number') {
      return primary;
    }
    const legacy = (container as unknown as Record<string, unknown>)[legacyKey];
    return typeof legacy === 'number' ? legacy : 1;
  };

  const border_left = resolveBorderValue(container.border_left, 'border-left');
  const border_top = resolveBorderValue(container.border_top, 'border-top');
  const border_right = resolveBorderValue(container.border_right, 'border-right');
  const border_bottom = resolveBorderValue(container.border_bottom, 'border-bottom');
  const borderColor = container.borderColor || '#FFFFFF';
  const containerBorderStyle = {
    borderLeftWidth: border_left,
    borderTopWidth: border_top,
    borderRightWidth: border_right,
    borderBottomWidth: border_bottom,
    borderStyle: 'solid' as const,
    borderColor,
  };

  // Note: Margins are applied by ElementRenderer, not here
  // ContainerRenderer only handles width/height based on stretch mode
  // Header/Footer containers are always vertically stretched
  const effectiveVerticalAlign = isHeaderOrFooter ? 'stretch' : container.verticalAlign;
  
  // Get user-defined margins for header/footer (needed for width calculation)
  const userMarginLeft = isHeaderOrFooter ? (container.marginLeft || 0) : 0;
  const userMarginRight = isHeaderOrFooter ? (container.marginRight || 0) : 0;
  
  const containerWidth = container.horizontalAlign === 'stretch' 
    ? (isHeaderOrFooter && (userMarginLeft !== 0 || userMarginRight !== 0))
      ? `calc(100% + ${-userMarginLeft}px + ${-userMarginRight}px)`
      : '100%'
    : container.width 
      ? `${container.width}px` 
      : 'auto';
  
  const containerHeight = effectiveVerticalAlign === 'stretch' 
    ? '100%' 
    : container.height 
      ? `${container.height}px` 
      : 'auto';
  
  // For header/footer: apply horizontal alignment via margins
  const getHorizontalMargins = () => {
    if (!isHeaderOrFooter) {
      return { marginLeft: undefined, marginRight: undefined };
    }
    
    // Get user-defined margins for header/footer
    const userMarginLeft = container.marginLeft || 0;
    const userMarginRight = container.marginRight || 0;
    const userMarginTop = container.marginTop || 0;
    const userMarginBottom = container.marginBottom || 0;
    
    // For stretched containers, just use user margins
    if (container.horizontalAlign === 'stretch') {
      return { 
        marginLeft: userMarginLeft ? `${userMarginLeft}px` : undefined,
        marginRight: userMarginRight ? `${userMarginRight}px` : undefined,
        marginTop: userMarginTop ? `${userMarginTop}px` : undefined,
        marginBottom: userMarginBottom ? `${userMarginBottom}px` : undefined
      };
    }
    
    // For aligned containers, combine alignment margins with user margins
    switch (container.horizontalAlign) {
      case 'center':
        return { 
          marginLeft: `calc(auto + ${userMarginLeft}px)`,
          marginRight: `calc(auto + ${userMarginRight}px)`,
          marginTop: userMarginTop ? `${userMarginTop}px` : undefined,
          marginBottom: userMarginBottom ? `${userMarginBottom}px` : undefined
        };
      case 'right':
        return { 
          marginLeft: `calc(auto + ${userMarginLeft}px)`,
          marginRight: userMarginRight ? `${userMarginRight}px` : 0,
          marginTop: userMarginTop ? `${userMarginTop}px` : undefined,
          marginBottom: userMarginBottom ? `${userMarginBottom}px` : undefined
        };
      default: // 'left'
        return { 
          marginLeft: userMarginLeft ? `${userMarginLeft}px` : 0,
          marginRight: `calc(auto + ${userMarginRight}px)`,
          marginTop: userMarginTop ? `${userMarginTop}px` : undefined,
          marginBottom: userMarginBottom ? `${userMarginBottom}px` : undefined
        };
    }
  };
  
  const { marginLeft: alignMarginLeft, marginRight: alignMarginRight, marginTop: alignMarginTop, marginBottom: alignMarginBottom } = getHorizontalMargins();

  return (
    <div
      className="relative"
      style={{
        width: containerWidth,
        height: containerHeight,
        marginLeft: alignMarginLeft,
        marginRight: alignMarginRight,
        marginTop: alignMarginTop,
        marginBottom: alignMarginBottom,
      }}
    >
      <div
        className={`border rounded transition-all h-full ${
          isSelected 
            ? 'border-primary ring-2 ring-primary/20 bg-primary/10' 
            : 'border-border hover:border-primary'
        }`}
        style={{
          paddingTop: container.paddingTop || 0,
          paddingBottom: container.paddingBottom || 0,
          paddingLeft: container.paddingLeft || 0,
          paddingRight: container.paddingRight || 0,
          ...containerBorderStyle,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            e.stopPropagation();
            onSelectElement(container.id);
          }
        }}
      >
        {isSelected && isDeletable && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(container.id);
            }}
            className="absolute -top-3 -right-3 z-50 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 shadow-lg"
            title="Container löschen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}

        {/* Native CSS Grid */}
        <div
          className="grid h-full"
          style={{
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gridTemplateRows: container.rowHeights?.join(' ') || `repeat(${rows}, 1fr)`,
            gap: `${gap}px`,
          }}
        >
          {Array.from({ length: rows * columns }).map((_, index) => {
            const row = Math.floor(index / columns);
            const col = index % columns;
            const cellKey = `${row}-${col}`;

            const child = children.find((c) => c.cellRow === row && c.cellColumn === col);
            const childIndex = child ? children.findIndex((c) => c.cellRow === row && c.cellColumn === col) : -1;
            const isCellSelected = selectedElementId === `${container.id}-cell-${cellKey}`;
            const isRowSelected = selectedElementId === `${container.id}-row-${row}`;
            
            // Get alignment from element, backgroundColor from child
            const cellHorizontalAlign = child?.element.horizontalAlign || 'left';
            const cellVerticalAlign = child?.element.verticalAlign || 'top';
            const rowBackgroundColor = container.rowBackgroundColors?.[row];
            const cellBackgroundColor = child?.backgroundColor 
              ?? rowBackgroundColor
              ?? 'transparent';

            return (
              <div
                key={cellKey}
                className={`relative border border-dashed transition-colors group min-h-0 ${
                  isRowSelected
                    ? 'border-green-500 ring-2 ring-green-500/30 bg-green-50/10'
                    : isCellSelected 
                    ? 'border-primary ring-2 ring-primary/20 bg-primary/10' 
                    : 'border-muted-foreground hover:border-primary hover:bg-accent/30'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  const cellId = `${container.id}-cell-${cellKey}`;
                  const rowId = `${container.id}-row-${row}`;
                  
                  // Drill-Up: Cell → Row → Container
                  if (isRowSelected) {
                    onSelectElement(container.id);
                  } else if (isCellSelected) {
                    onSelectElement(rowId);
                  } else if (e.target === e.currentTarget) {
                    // Only select cell if clicked directly on it (not on child element)
                    onSelectElement(cellId);
                  }
                }}
                style={{
                  display: 'grid',
                  alignItems: cellVerticalAlign === 'middle' ? 'center' :
                             cellVerticalAlign === 'bottom' ? 'end' : 
                             cellVerticalAlign === 'stretch' ? 'stretch' : 'start',
                  justifyItems: cellHorizontalAlign === 'center' ? 'center' :
                    cellHorizontalAlign === 'right' ? 'end' : 
                    cellHorizontalAlign === 'stretch' ? 'stretch' : 'start',
                  overflow: 'visible',
                  backgroundColor: cellBackgroundColor,
                }}
              >
                {child ? (
                  <ElementRenderer
                    element={child.element}
                    globalFontFamily={fontFamily}
                    globalFontSize={fontSize}
                    globalFontColor={fontColor}
                    containerPath={child.element.type === 'container' && childIndex >= 0 
                      ? [...containerPath, childIndex] 
                      : containerPath}
                    selectedElementId={selectedElementId}
                    onSelectElement={onSelectElement}
                    onInsertClick={onInsertClick}
                    onDelete={onDelete}
                    cellInfo={{ containerId: container.id, row, col }}
                    rootContainerIds={rootContainerIds}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <button
                      className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-primary/90 transition-all shadow-lg pointer-events-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        onInsertClick(containerPath, row, 'below', row, col);
                      }}
                      title="Element einfügen"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
